import { LogoItemType, LogosCarouselType } from './type';
import { Box, Typography } from '@mui/material';
import Image from 'next/image';

type LogoSizeKey = NonNullable<LogosCarouselType['styling']['logoSize']>;

type LogoSizeConfig = {
  w: { xs: number; md: number };
  h: { xs: number; md: number };
  sizes: string;
};

const LOGO_SIZES: Record<LogoSizeKey, LogoSizeConfig> = {
  xs: { w: { xs: 50, md: 60 }, h: { xs: 20, md: 24 }, sizes: '60px' },
  sm: { w: { xs: 90, md: 120 }, h: { xs: 36, md: 48 }, sizes: '120px' },
  md: { w: { xs: 110, md: 160 }, h: { xs: 44, md: 64 }, sizes: '160px' },
  lg: { w: { xs: 130, md: 200 }, h: { xs: 52, md: 80 }, sizes: '200px' },
  xl: { w: { xs: 180, md: 260 }, h: { xs: 72, md: 104 }, sizes: '260px' },
};

const DEFAULT_LOGO_SIZE: LogoSizeConfig = {
  w: { xs: 100, md: 140 },
  h: { xs: 40, md: 56 },
  sizes: '140px',
};

type LogoItemProps = {
  logo: LogoItemType;
  grayscale: boolean;
  pauseOnHover: boolean;
  logoSize?: LogoSizeKey;
};

const LogoItem = ({
  logo,
  grayscale,
  pauseOnHover,
  logoSize,
}: LogoItemProps) => {
  const size = logoSize ? LOGO_SIZES[logoSize] : DEFAULT_LOGO_SIZE;

  const imageBox = (
    <Box
      className={'tb-logo-carousel-image-box'}
      sx={{
        position: 'relative',
        height: { xs: size.h.xs, md: size.h.md },
        width: { xs: size.w.xs, md: size.w.md },
        filter: grayscale ? 'grayscale(100%)' : 'none',
        opacity: grayscale ? 0.65 : 1,
        transition: 'filter 0.3s ease, opacity 0.3s ease',
        '&:hover':
          pauseOnHover || logo.url
            ? {
                filter: 'none',
                opacity: 1,
              }
            : {},
      }}
    >
      <Image
        src={logo.image.url}
        alt={logo.title || logo.image.altText}
        fill
        sizes={size.sizes}
        style={{ objectFit: 'contain' }}
        placeholder={'empty'}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        px: { xs: 3, md: 4 },
        flex: '0 0 auto',
      }}
    >
      {logo.url ? (
        <Box
          component="a"
          href={logo.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {imageBox}
          {logo.title && (
            <Typography
              variant="caption"
              sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
            >
              {logo.title}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {imageBox}
          {logo.title && (
            <Typography
              variant="caption"
              sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
            >
              {logo.title}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default LogoItem;
